import { Component, EventEmitter, OnInit, Output, Inject, AfterViewInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ConfigService } from 'src/app/shared/services/config.service';
import { ErrorDialogService } from 'src/app/shared/errors/error-dialog.service';
import {
  mandatoryLineFields,
  exportCustomerInvoiceItemsToRemove,
} from 'src/app/modules/mapping/custom-mapping/config/mapping-config';
import { SpecificationService } from '../../../services/specification.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { SnackBarService } from 'src/app/shared/snack-bar/services/snack-bar.service';
import { basicSnackBarConfig } from 'src/app/utils/default-configs/configuration';

@Component({
  selector: 'app-add-edit-invoice-specification',
  templateUrl: './add-edit-invoice-specification.component.html',
  styleUrls: ['./add-edit-invoice-specification.component.css'],
})
export class AddEditInvoiceSpecificationComponent implements OnInit, AfterViewInit {
  mappedFields = mandatoryLineFields;
  unMappedFields = ['Blank', ...exportCustomerInvoiceItemsToRemove];
  public lineItemMappingForm: FormGroup;
  specMode: string = 'Create_New';
  mappingOptions = [];
  mandatoryFields = [];
  selectedSpec;
  isDirty = false;
  specHeader = 'Create Invoice Specification';
  availableFieldsSubject = new BehaviorSubject(false);
  isUpdate: boolean = false;

  @Output() dirtyEvent = new EventEmitter<boolean>();

  constructor(
    private fb: FormBuilder,
    private configService: ConfigService,
    private errorDialogService: ErrorDialogService,
    private dialogRef: MatDialogRef<AddEditInvoiceSpecificationComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData,
    private specificationService: SpecificationService,
    private snackBarService: SnackBarService
  ) {}

  ngOnInit(): void {
    this.mandatoryFields = [...mandatoryLineFields];
    this.unMappedFields = ['Blank', ...exportCustomerInvoiceItemsToRemove];
    this.mappedFields = [...this.mandatoryFields];

    this.initializationformGroup();
    if (this.dialogData.action === 'update') {
      this.isUpdate = true;
      this.specHeader = 'Update Invoice Specification';
      this.specSelectEvent({ value: this.dialogData.spec.specifications_id });
    } else if (this.dialogData.action === 'upload') {
      this.executeOnResponse([this.dialogData.spec]);
    } else {
      this.isUpdate = false;
      this.specHeader = 'Create Invoice Specification';
    }
  }

  ngAfterViewInit() {
    this.isLineItemMappingUnChanged();
  }

  isLineItemMappingUnChanged() {
    setTimeout(() => {
      setTimeout(
        function () {
          this.lineItemMappingForm.valueChanges.subscribe(() => {
            this.isDirty = true;
            this.dirtyEvent.emit(this.isDirty);
          });
        }.bind(this),
        0
      );
    }, 1000);
  }

  initializationformGroup() {
    this.lineItemMappingForm = this.fb.group({
      specifications_id: ['', Validators.required],
      delimiter: ['', Validators.required],
      headers: [false],
    });
  }

  drop(event: CdkDragDrop<string[]>) {
    this.lineItemMappingForm.markAsDirty();
    this.isDirty = true;
    this.dirtyEvent.emit(this.isDirty);
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      if (this.isFieldMandatory(event.previousContainer.data[event.previousIndex])) {
        return;
      } else {
        const previousIndexValue = event.previousContainer.data[event.previousIndex];
        if (previousIndexValue === 'Blank' && event.previousContainer.id === 'cdk1') {
          this.mappedFields.splice(event.currentIndex, 0, previousIndexValue);
          return;
        }
        if (previousIndexValue === 'Blank' && event.previousContainer.id === 'cdk2') {
          this.mappedFields.splice(event.previousIndex, 1);
          return;
        }

        transferArrayItem(
          event.previousContainer.data,
          event.container.data,
          event.previousIndex,
          event.currentIndex
        );
      }
    }
  }

  isFieldMandatory(field) {
    return this.mandatoryFields.includes(field);
  }

  specModeChangeEvent(event) {
    this.specMode = event?.value;
    this.selectedSpec = '';
    this.lineItemMappingForm.reset();
    this.mappedFields = [];
    if (this.specMode === 'Create_New') {
      this.unMappedFields = ['Blank', ...exportCustomerInvoiceItemsToRemove];
      this.mappedFields = [...this.mandatoryFields];
    } else {
      this.unMappedFields = ['Blank'];
      this.mappedFields = [];
    }
  }

  specSelectEvent(spec) {
    this.lineItemMappingForm.reset();
    this.getSelectMappingOption(spec.value);
  }

  isSpecEditMode() {
    return this.specMode === 'Edit';
  }

  getMappingOptions() {
    const requestBody = { config_queries: [{ config_id: 'customer_invoice_specifications' }] };
    this.configService.getLineItemsSpecifications(requestBody).subscribe(data => {
      this.mappingOptions = data[0];
      this.isDirty = false;
      this.dirtyEvent.emit(this.isDirty);
    });
  }

  getSelectMappingOption(specId) {
    this.lineItemMappingForm.reset();
    const requestBody = {
      config_queries: [
        {
          config_id: 'customer_invoice_specifications',
          specifications_id: specId,
        },
      ],
    };
    this.configService.getLineItemsSpecifications(requestBody).subscribe(response => {
      this.executeOnResponse(response);
    });
  }

  executeOnResponse(response) {
    this.mappedFields = [];
    this.mappedFields = response[0].data[0].csvColumns.map(function (item) {
      return item === 'TBA' ? 'Blank' : item;
    });

    this.lineItemMappingForm.patchValue({ specifications_id: response[0]?.specifications_id });
    this.lineItemMappingForm.patchValue({ delimiter: response[0]?.data[0]?.delimiter });
    this.lineItemMappingForm.patchValue({ headers: response[0]?.data[0]?.headers });
    this.unMappedFields = response[0].data[0].availableColumns.map(function (item) {
      return item === 'TBA' ? 'Blank' : item;
    });
  }

  getAllShipFields(mappingData) {
    const shipFields = [];

    for (const mappingObj of mappingData) {
      const fieldName = mappingObj.field_name;
      shipFields.push(fieldName);
    }
    return shipFields;
  }

  filterTwoArrayNotIncluded(arr1, arr2) {
    const unMappedWithBlank = ['Blank'];
    const arrayUnmapped = arr1.filter(x => !arr2.includes(x));
    return unMappedWithBlank.concat(arrayUnmapped);
  }

  saveLineItemConfigMapping() {
    if (!this.lineItemMappingForm.valid) {
      this.errorDialogService.openDialog('alert.enterAllMandatory', false);
      return;
    }

    const blankCheckedMapFields = this.mappedFields.map(function (item) {
      return item === 'Blank' ? 'TBA' : item;
    });

    const dataFields = {
      delimiter: this.lineItemMappingForm.value.delimiter.trim(),
      availableColumns: this.unMappedFields,
      csvColumns: blankCheckedMapFields,
      headers: this.lineItemMappingForm.value.headers,
    };

    const options = {
      config_id: 'customer_invoice_specifications',
      specifications_id: this.lineItemMappingForm.value.specifications_id,
      use_for_automated_upload: true,
      data: dataFields,
    };

    this.configService.saveMappingOption(options).subscribe(() => {
      this.snackBarService.createSnackBar(
        basicSnackBarConfig.mappingShipmentSpecificationSave?.message,
        basicSnackBarConfig.mappingShipmentSpecificationSave?.horizontalPosition,
        basicSnackBarConfig.mappingShipmentSpecificationSave?.verticalPosition,
        basicSnackBarConfig.mappingShipmentSpecificationSave?.duration
      );
      this.getMappingOptions();
      this.specificationService.onInvoiceSpecUpdateAndCreation.next(false);
      this.dialogRef.close({ value: true });
    });
  }

  canDeactivate() {
    return this.isDirty;
  }
}
